<script lang="ts" setup>
import { useRouter } from "vue-router";
import { useDialog } from "primevue/usedialog";
import { formData } from "@/components/RgAddPatientComponent.vue";
import IPPreviewList from "@/components/RgPreviewList.vue";
import JSZip from "jszip";
import { DocumentContext, ProjectGeneralData, createImageDataFromWasm, useWasmEngine, IEngineGenerated, UniversalFilePicker } from "@xlabmedical/medic-web-engine";
import { devLogger, useModals, useServerApi, MContainer, useAssets, useAuth, Exception, success, MContainerRow, error, dicomFormats } from "@xlabmedical/medic-web-framework";
import {
    projectGeneralDataModel,
    writeProjectObject,
    createMobileForUpload,
    createProjectZip,
    ImplantPlannerProjectData,
    prepareProjectData,
    ProjectModel,
    getPreviewProjectFromZip,
    uploadProjectFromZip,
    analyzeDicomSeries,
} from "@xlabmedical/implant-planner";
import { getDicomMiddleSlice, getDicomImportFailureSummary, isDicomImportCandidate, type DicomSeriesSlice, type DicomImportFile } from "@xlabmedical/medic-web-framework";
import { FileElement } from "@xlabmedical/medic-api";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import type { DynamicDialogOptions } from "primevue/dynamicdialogoptions";
import { PatientListModel } from "@/models/PatientListModel";
import RgImportMenu from "@/components/RgImportMenu.vue";
import ImportImage from "@/components/RgImportImageComponent.vue";
import ImportDocument from "@/components/RgImportDocumentComponent.vue";
import StlObject from "@/components/RgImportStlComponent.vue";

import { FileUploadUploaderEvent } from "primevue/fileupload";
import Divider from "primevue/divider";
import { useI18n } from "vue-i18n";
import EditPatientDialog from "@/components/RgEditPatientComponent.vue";
import Dialog from "primevue/dialog";
import ProgressBar from "primevue/progressbar";

const { t } = useI18n();
const assetApi = useAssets();

enum UIState {
    UPLOADING_OBJECT,
    UPLOADING_DICOM,
    UPLOADING_IMAGE,
    UPLOADING_DOCUMENT,
    LOADING,
}

const state = ref<UIState>(UIState.UPLOADING_DICOM);

const dialogRef = inject("dialogRef") as DynamicDialogOptions;

const params = dialogRef.value.data as {
    projects: ProjectModel[];
    selectedPatient: PatientListModel;
    patientData?: formData;
    addNewPatientFlag?: boolean;
};

const emits = defineEmits(["updatePatients", "done"]);

const processingContext = ref(new DocumentContext(assetApi.getAssetUrl("")));

const router = useRouter();
const dialog = useDialog();
const confirm = useConfirm();
const toast = useToast();
const api = useServerApi();
const assetPlugin = useAssets();
let module: any;
let engineInstance: IEngineGenerated;

const auth = useAuth();
const license = auth.getSessionLicence();
const user = auth.getUser();

let PBD = new Date(1970, 1, 1).toISOString();
if (params.patientData?.dateOfBirth) {
    PBD = params.patientData?.dateOfBirth.toISOString();
}
const patientData = ref({
    firstName: params.patientData?.firstName,
    lastName: params.patientData?.lastName || "",
    notes: params.patientData?.notes,
    dateOfBirth: PBD,
    extra: {
        patientName: params.patientData?.firstName,
        study_instance_UID: "",
    },
});

let dicomPatientName: string | null = null;
let dicomPatientFirstName: string | null = null;
let dicomPatientLastName: string | null = null;
let dicomPatientBD: Date | null = null;

const isEngineReady = ref(false);

onMounted(async () => {
    await ensureEngineReady();
});

onUnmounted(() => {
    if (processingContext.value) {
        processingContext.value.cleanup();
        processingContext.value = null;
    }
});

let countProj = 1;
const projectName = ref("New project");
if (!params.addNewPatientFlag) {
    while (params.projects.find((f) => f.name === projectName.value + ".zip")) {
        projectName.value = (params.selectedPatient.firstName + " " + params.selectedPatient.lastName || "").replaceAll(" ", "_") + "_project" + countProj;
        countProj++;
    }
}

let allProjects = 1;
let count = 0;
const uploadProgress = ref(0);
const uploadProgressVisible = ref(false);
const uploadProgressVisible1 = ref(false);
const uploadProgressVisible2 = ref(false);
const projectNameProgressBar = ref("");
const processingProgress = ref(0);
const isDeterminate = ref(true);

const closeUploadProgress = () => {
    uploadProgressVisible1.value = false;
    uploadProgressVisible2.value = false;
    uploadProgressVisible.value = false;
};

const notifyUploadError = (summary: string, detail?: Exception | string) => {
    closeUploadProgress();
    error(summary, detail as Exception);
};

let uploadDicomWasmClass!: IEngineGenerated.prototype.xLoadDicom;
let previewGeneratorWasmClass!: IEngineGenerated.prototype.xDicomPreviewGenerator;
let fileUtilsWasmClass!: IEngineGenerated.prototype.xFileUtilsWasm;

const dicomTags = {
    imageType: { group: 0x0008, element: 0x0008 },
    patientName: { group: 0x0010, element: 0x0010 },
    patientId: { group: 0x0010, element: 0x0020 },
    studyId: { group: 0x0020, element: 0x0010 },
    studyInstanceUID: { group: 0x0020, element: 0x000d },
    patientBirthDate: { group: 0x0010, element: 0x0030 },
    seriesInstanceUID: { group: 0x0020, element: 0x000e },
    studyDate: { group: 0x0008, element: 0x0020 },
    studyDescription: { group: 0x0008, element: 0x1030 },
    institutionName: { group: 0x0008, element: 0x0080 },
    referringPhysician: { group: 0x0008, element: 0x0090 },
    modality: { group: 0x0008, element: 0x0060 },
    seriesDescription: { group: 0x0008, element: 0x103e },
    imagePatientPosition: { group: 0x0020, element: 0x0032 },
    numberOfFrames: { group: 0x0028, element: 0x0008 },
    sliceThickness: { group: 0x0018, element: 0x0050 },
    rows: { group: 0x0028, element: 0x0010 },
    column: { group: 0x0028, element: 0x0011 },
    pixelSpacing: { group: 0x0028, element: 0x0030 },
    imageOrientationPatient: { group: 0x0020, element: 0x0037 },
    instanceNumber: { group: 0x0020, element: 0x0013 },
    kVp: { group: 0x0018, element: 0x0060 },
    manufacturer: { group: 0x0008, element: 0x0070 },
    manufacturerModelName: { group: 0x0008, element: 0x1090 },
};

export interface previewObject {
    fName: string;
    path: string;
    seriesId: string;
    modality: string;
    description: string;
    position: [number, number, number];
    thickness: number;
    dimensions: [number, number];
    pixelDimension: [number, number];
    directionX: [number, number, number];
    directionY: [number, number, number];
    instanceNumber: number;
    manufacturer: string;
    manufacturerModelName: string;
    studyDate: [number, number, number];
    preview: string;
    selectedFlag: Ref<boolean>;
}

const previewList: Ref<previewObject[]> = ref([]);
let unFilteredFilesUpload: any[] = [];
let filesUpload: any;

type Slice = DicomSeriesSlice;

let filesMap: Map<string, Map<string, Slice[]>> = new Map();
let filesByPath: Map<string, any[]> | null = null;

const readFileAsArrayBuffer = (file: File): Promise<ArrayBuffer> => {
    return new Promise<ArrayBuffer>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = function (event) {
            const arrayBuffer = event.target?.result as ArrayBuffer;
            resolve(arrayBuffer);
        };
        reader.onerror = (event) => {
            reject(event.target?.error);
        };
        reader.readAsArrayBuffer(file);
    });
};

const ensureEngineReady = async () => {
    if (isEngineReady.value) {
        return true;
    }
    devLogger.debug("Waiting for engine to load...");
    try {
        module = await useWasmEngine();
        if (!module) {
            console.error("WASM module is null");
            return false;
        }
        engineInstance = new IEngineGenerated(module as any);
        processingContext.value.module = module;
        processingContext.value.setExternalEngineInstance(engineInstance);
        processingContext.value.xWasmDocument = new engineInstance.xWasmDocumentJS();
        processingContext.value.makeDir("/app");
        isEngineReady.value = true;
        return true;
    } catch (err) {
        console.error("Failed to initialize WASM engine", err);
        return false;
    }
};

const getAvailableProjectName = async (baseName: string) => {
    let countProjId = 1;
    let name = baseName;
    const usedNames = new Set<string>(params.projects.map((p) => p.name));
    const studyInstanceUID = params.selectedPatient?.patientName || patientData.value.extra.study_instance_UID;
    if (studyInstanceUID) {
        try {
            const r = await api.getProjectFileList<FileElement[]>(studyInstanceUID);
            for (const file of r.data) {
                usedNames.add(file.name);
            }
        } catch (e) {
            console.warn("Failed to load project list for name check", e);
        }
    }
    while (usedNames.has(name + ".zip")) {
        name = baseName + "_" + countProjId;
        countProjId++;
    }
    name = name.replaceAll("__", "_");
    return name;
};

const getFileKey = (file: File) => {
    const relPath = file.webkitRelativePath;
    return relPath && relPath.length > 0 ? relPath : file.name;
};

const buildFilesByPathMap = (files: any[]) => {
    const map = new Map<string, any[]>();
    for (const file of files) {
        const key = getFileKey(file);
        const list = map.get(key);
        if (list) {
            list.push(file);
        } else {
            map.set(key, [file]);
        }
    }
    return map;
};

const ensureWasmDir = (path: string) => {
    try {
        module.FS.mkdir(path);
    } catch (e) {
        devLogger.debug("EXCEPTION: mkdir failed", e);
    }
};

const safeUnlink = (path: string) => {
    try {
        module.FS.unlink(path);
    } catch (e) {
        devLogger.debug("EXCEPTION - Unlinking path", path, e);
    }
};

const safeRmdir = (path: string) => {
    try {
        module.FS.rmdir(path);
    } catch (e) {
        devLogger.debug("EXCEPTION - remove dir", path, e);
    }
};

const parseDicomPatientName = (rawName: string) => {
    const parts = rawName
        .split("^")
        .map((p) => p.trim())
        .filter(Boolean);
    const lastName = parts[0] || "";
    const firstName = parts[1] || "";
    const fullName = parts.length ? parts.join(" ") : rawName.replaceAll("^", " ").trim();
    return {
        firstName,
        lastName,
        fullName,
    };
};

const parseDicomDate = (dicomDate: any) => {
    if (!dicomDate || dicomDate.length < 3) {
        return null;
    }
    const year = Number(dicomDate[0]);
    const month = Number(dicomDate[1]);
    const day = Number(dicomDate[2]);
    if (!year || !month || !day) {
        return null;
    }
    return new Date(year, month - 1, day);
};

const formatDateToYYYYMMDD = (date: Date | null) => {
    if (date == null) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}${month}${day}`;
};

const convertDateToPatientStudyDateFormat = (date: Date | null) => {
    if (date == null) return "";
    return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
};

const createRGDicomData = (data: {
    patientName?: string;
    patientId?: string;
    patientStudyId?: string;
    patientDob?: string;
    patientRef?: string;
    patientInst?: string;
    patientStudyDescr?: string;
    patientStudyDate?: string;
    patientSeriesDescr?: string;
    modality?: string;
    patientStudyUid?: string;
    isCommandLine?: string | number;
}) => {
    const value = (item: unknown) => {
        if (typeof item === "string") {
            return item.trim();
        }
        if (item === undefined || item === null) {
            return "";
        }
        return String(item).trim();
    };
    return [
        `PATIENT_NAME=${value(data.patientName)}`,
        `PATIENT_ID=${value(data.patientId)}`,
        `PATIENT_STUDY_ID=${value(data.patientStudyId)}`,
        `PATIENT_DOB=${value(data.patientDob)}`,
        `PATIENT_REF=${value(data.patientRef)}`,
        `PATIENT_INST=${value(data.patientInst)}`,
        `PATIENT_STUDY_DESCR=${value(data.patientStudyDescr)}`,
        `PATIENT_STUDY_DATE=${value(data.patientStudyDate)}`,
        `PATIENT_SERIES_DESCR=${value(data.patientSeriesDescr)}`,
        `MODALITY=${value(data.modality)}`,
        `PATIENT_STUDY_UID=${value(data.patientStudyUid)}`,
        `IS_COMMAND_LINE=${value(data.isCommandLine)}`,
    ].join("\n");
};

const confirmation = async (event: FileUploadUploaderEvent): Promise<boolean> => {
    const startState = state.value;
    state.value = UIState.LOADING;
    let didUpload = false;

    if (!params.addNewPatientFlag) {
        for (let f of params.projects) {
            if (f.projectFilename == projectName.value + ".zip") {
                confirm.require({
                    message: t("web-rg.overwriteProjectPrompt", { projectName: projectName.value }),
                    header: t("web-rg.overwrite"),
                    icon: "pi pi-exclamation-triangle",
                    accept: async () => {
                        info(t("web-rg.projectWillBeOverwritten"));
                        confirm.close();
                        await uploadFiles(event);
                        didUpload = true;
                        return;
                    },
                    reject: () => {
                        confirm.close();
                        return;
                    },
                });
                return false;
            }
        }
        await uploadFiles(event);
        didUpload = true;
    } else {
        await uploadFiles(event);
        didUpload = true;
    }
    state.value = startState;
    return didUpload;
};

const createPatientProject = async (studyInstanceUID: string) => {
    try {
        await api.addProject(studyInstanceUID, JSON.stringify(patientData.value));
        return true;
    } catch (err) {
        const apiMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
        const fallbackMessage = (err as { message?: string })?.message;
        notifyUploadError(t("web-rg.errorWhileUploadingProject"), apiMessage ?? fallbackMessage ?? (err as Exception));
        return false;
    }
};

const getSelectionKey = (selection: File[]) =>
    selection.map((file) => `${file.name}|${file.size}|${(file as File & { webkitRelativePath?: string }).webkitRelativePath || ""}`).join("::");

const isFolderSelection = (selection: File[]) =>
    selection.some((file) => {
        const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath;
        return !!rel && rel.includes("/");
    });

const lastAutoUploadedKey = ref<string | null>(null);

const onUploadFiles = async (selection: File[]) => {
    const uploadEvent = { files: selection } as FileUploadUploaderEvent;
    try {
        if (lastAutoUploadedKey.value && getSelectionKey(selection) === lastAutoUploadedKey.value) {
            return;
        }
        await confirmation(uploadEvent);
    } catch (err) {
        notifyUploadError(t("web-rg.errorWhileUploadingProject"), err as Exception);
    }
};

const uploadFiles = async (event: any) => {
    let t0 = performance.now();

    const engineReady = await ensureEngineReady();
    if (!engineReady) {
        notifyUploadError(t("web-rg.errorWhileUploadingProject"), "WASM engine not ready");
        return;
    }
    // devLogger.debug("Event:", event);
    let fileType = event.files[0].name.slice(-3);
    // devLogger.debug("FIleType: ", fileType);
    // const potentiallyAllowableTypes = [".gz", "nii", "rrd", "mha"];
    const potentiallyAllowableTypes = [".gz", "nii"];
    // const allowableTypes = ["nii.gz", "nii", "rrd", "mha"];
    const allowableTypes = ["nii.gz", "nii"];
    isDeterminate.value = false;
    processingProgress.value = 0;
    if (fileType === "zip") {
        //////////////////////////////////////////////////////////////////////////////////////////////////////////////////
        //////////////////////////////////////////////////////////////////////////////////////////////////////////////////
        ///////////////////////////////////////// UPLOAD PROJECT FROM ZIP ////////////////////////////////////////////////
        //////////////////////////////////////////////////////////////////////////////////////////////////////////////////
        //////////////////////////////////////////////////////////////////////////////////////////////////////////////////
        // TODO code duplicates!

        uploadProgressVisible.value = true;

        // Set up project name (2.9.2025: Get project name from unzipped file - sync with RG)
        const d = await getPreviewProjectFromZip(event.files[0]);
        if (d) {
            let newProjectFileName = d?.data.filename.replace(".mobile.json", "");
            let studyInstanceUID = patientData.value.extra.study_instance_UID;
            if (!params.addNewPatientFlag && params.selectedPatient) {
                studyInstanceUID = params.selectedPatient.patientName;
            } else if (studyInstanceUID == "") {
                if (d.data.patientName) {
                    patientData.value.firstName = d.data.patientName;
                    patientData.value.dateOfBirth = d.data.pBDate;
                    studyInstanceUID = d.data.patientName.replaceAll(" ", "_") + Date.now();
                } else if (params.patientData) {
                    let pName = params.patientData?.firstName + " " + params.patientData?.lastName;
                    studyInstanceUID = pName.replaceAll(" ", "_") + Date.now();
                } else {
                    studyInstanceUID = "Patient_" + Date.now();
                }
                // Add new patient if needed
                if (params.addNewPatientFlag) {
                    patientData.value.extra.study_instance_UID = studyInstanceUID;
                    const created = await createPatientProject(studyInstanceUID);
                    if (!created) return;
                    router.push({
                        name: "Patients",
                        params: {
                            id: studyInstanceUID,
                        },
                    });
                }
            }

            // devLogger.debug("newProjectFileName: ", newProjectFileName);

            // Set up progress tracking
            uploadProgressVisible1.value = false;
            uploadProgressVisible2.value = true;
            projectNameProgressBar.value = newProjectFileName;

            const throttleUpdate = useThrottleFn((progress: number) => {
                uploadProgress.value = Math.ceil(progress * 100) - 1;
            }, 250);

            newProjectFileName = await getAvailableProjectName(newProjectFileName);
            const uploadedZip = await uploadProjectFromZip(api, event.files[0], studyInstanceUID, newProjectFileName, throttleUpdate, true);

            success(t("web-rg.projectUploadedSuccessfully", { name: newProjectFileName }));
            // Clean up
            uploadProgress.value = 100;
            uploadProgress.value = 0;
            uploadProgressVisible2.value = false;
            uploadProgressVisible.value = false;
            // dialogRef.value.close();
            if (uploadedZip) {
                // Emit update patients
                emits("updatePatients", studyInstanceUID);
                return;
            }
        }
        //////////////////////////////////////////////////////////////////////////////////////////////////////////////////
        //////////////////////////////////////////////////////////////////////////////////////////////////////////////////
        //////////////////////////////////////////////////////////////////////////////////////////////////////////////////
        //////////////////////////////////////////////////////////////////////////////////////////////////////////////////
        const zip = new JSZip();
        await zip.loadAsync(event.files[0]);
        const blobArr = [];
        if (Object.keys(zip.files).length == 0) {
            console.warn("Empty zip folder");
            uploadProgressVisible.value = false;
            toast.add({ severity: "error", summary: t("web-rg.dataNotUploaded"), detail: t("web-rg.zipIsEmpty"), life: 3000 });
            return;
        }
        // Keep the DICM signature fast path, and let GDCM validate plausible raw datasets.
        for (let f of Object.entries(zip.files)) {
            if (!f[1].dir && !f[0].split("/").includes("__MACOSX") && !f[0].endsWith(".DS_Store")) {
                const uZipF = new Int8Array(await zip.files[f[0]].async("uint8array"));
                if (isDicomImportCandidate(f[0], uZipF)) {
                    let webkitPathParts = f[0].split("/");
                    const bFile = {
                        data: uZipF,
                        name: webkitPathParts[webkitPathParts.length - 1],
                        webkitRelativePath: f[0],
                    };
                    blobArr.push(bFile);
                } else {
                    // console.warn("File is not DICOM: ", f[0]);
                }
            }
        }
        filesUpload = blobArr;

        uploadProgressVisible.value = false;

        unFilteredFilesUpload = [...filesUpload];
        filesUpload = [];
        // emits("done");
    } else if (potentiallyAllowableTypes.includes(fileType)) {
        fileUtilsWasmClass = new engineInstance.xFileUtilsWasm();
        try {
            // filter list!!
            let filteredList = [];
            for (let f of event.files) {
                let suffix = f.name.slice(-3);
                if (suffix == ".gz") {
                    suffix = f.name.slice(-6);
                }
                if (allowableTypes.includes(suffix)) {
                    filteredList.push(f);
                }
            }

            allProjects = filteredList.length;
            count = 0;
            newPatientLoaded = false;
            let studyInstanceUID = patientData.value.extra.study_instance_UID;
            let projName = "";
            if (!params.addNewPatientFlag && params.selectedPatient) {
                studyInstanceUID = params.selectedPatient.patientName;
                projName = params.selectedPatient.firstName + "_" + params.selectedPatient.lastName;
            }
            for (let f of filteredList) {
                count++;
                uploadProgressVisible.value = true;
                uploadProgressVisible1.value = true;
                processingProgress.value = Math.floor(((count - 1) / allProjects) * 10); // Progress: 0-70% for processing each file

                let newProjectFileName = "";
                if (allProjects > 1) {
                    if (projName == "") {
                        projName = projectName.value;
                    }
                    let bName = projName + "_project " + count;
                    newProjectFileName = await getAvailableProjectName(bName);
                } else {
                    newProjectFileName = await getAvailableProjectName(projectName.value);
                }
                let pName = params.patientData?.firstName + " " + params.patientData?.lastName;

                let path = f.name;
                let buff = new Int8Array(await readFileAsArrayBuffer(f));
                processingProgress.value = Math.floor(((count - 1) / allProjects) * 20); // Reading file
                ensureWasmDir("/app");
                path = "/app/" + path;
                module.FS.writeFile(path, buff);

                fileUtilsWasmClass.ImageDataLoad(path);
                processingProgress.value = Math.floor(((count - 1) / allProjects) * 35); // Loading image data

                const iDataView: IEngineGenerated.prototype.ImageDataJSView = fileUtilsWasmClass.GetImageDataView();

                const image = createImageDataFromWasm(iDataView);
                iDataView.delete();
                // devLogger.debug("pixelData", image.getPointData().getScalars().getData());
                devLogger.debug("dims", image.getDimensions());
                devLogger.debug("spac", image.getSpacing());
                devLogger.debug("org", image.getOrigin());

                let context = new DocumentContext(assetPlugin.getAssetUrl(""));
                context.user = user;
                context.setResourceUrl("/resources/");
                context.setModalsInstance(useModals());
                context.setImageData(image);
                context.replaceVolumeObject({ render: false });
                context.setExternalEngineInstance(engineInstance); // needed to fully load the engine
                processingProgress.value = Math.floor(((count - 1) / allProjects) * 45); // Context prepared

                if (params.addNewPatientFlag && !newPatientLoaded) {
                    newPatientLoaded = true;
                    if (studyInstanceUID == "") {
                        studyInstanceUID = pName.replaceAll(" ", "_") + Date.now();
                        patientData.value.extra.study_instance_UID = studyInstanceUID;
                        const created = await createPatientProject(studyInstanceUID);
                        if (!created) return;
                        router.push({
                            name: "Patients",
                            params: {
                                id: studyInstanceUID,
                            },
                        });
                    }
                }

                const pMax: [number, number, number] = context.getImageData().getDimensions();
                const today = new Date();
                const data: ProjectGeneralData = {
                    generalNotes: "",
                    patientData: [pName.trim(), (studyInstanceUID || "").trim(), formatDateToYYYYMMDD(pBDate)].join("\n"),
                    seriesData: ["", "", " 0kV "].join("\n"),
                    dicomData: createRGDicomData({
                        patientName: pName,
                        patientId: studyInstanceUID,
                        patientStudyId: studyInstanceUID,
                        patientDob: formatDateToYYYYMMDD(pBDate),
                        patientRef: "",
                        patientInst: "",
                        patientStudyDescr: "",
                        patientStudyDate: "",
                        patientSeriesDescr: "",
                        modality: "",
                        patientStudyUid: studyInstanceUID,
                        isCommandLine: 0,
                    }),
                    modified: {
                        year: today.getFullYear(),
                        month: today.getMonth(),
                        day: today.getDate(),
                        hour: today.getHours(),
                        minutes: today.getMinutes(),
                        seconds: today.getSeconds(),
                    },
                    projectVersion: license?.versionType || 1,
                    projectCropped: 0,
                    croppedPointMin: [0, 0, 0],
                    croppedPointMax: pMax,
                    useUSToothMap: context.settings.general.useUSStyleToothChart,
                };
                const d2 = writeProjectObject(projectGeneralDataModel, data);
                const originalData: Omit<ImplantPlannerProjectData, "imageData"> = {
                    pr: d2,
                    stlInfo: {
                        objectType: "object",
                        data: {},
                    },
                    mobile: {
                        objectType: "object",
                        data: {},
                    },
                };

                processingProgress.value = Math.floor(((count - 1) / allProjects) * 55); // Preparing project data
                if (context.patientInfo == undefined) {
                    context.patientInfo = {
                        patientID: studyInstanceUID,
                        patientName: pName,
                        patientBirthDate: pBDate,
                    };
                }
                const projectData = await prepareProjectData(context, originalData, true);
                processingProgress.value = Math.floor(((count - 1) / allProjects) * 65); // Project data prepared

                if (!projectData) {
                    notifyUploadError(t("web-rg.errorWhileUploadingProject"), t("web-rg.projectDataNotPrepared"));
                    continue;
                }

                projectNameProgressBar.value = newProjectFileName;

                const throttleUpdate = useThrottleFn((progress: number) => {
                    uploadProgress.value = Math.ceil(((count - 1) / allProjects) * 100 + (progress * 100) / allProjects) - 1;
                }, 250);

                // devLogger.debug("Prepare for save, stduyInstanceUID: ", studyInstanceUID, ", newProjName", newProjectFileName);

                processingProgress.value = Math.floor(((count - 1) / allProjects) * 85); // Project data prepared
                const zip = await createProjectZip(newProjectFileName, projectData);

                processingProgress.value = Math.floor(((count - 1) / allProjects) * 95); // Project data prepared
                uploadProgressVisible1.value = false;
                uploadProgressVisible2.value = true;

                // saveAs(zip, "test0.zip")
                const createDate = new Date().toISOString();

                let projectUploaded = false;
                try {
                    await api.uploadFile(
                        studyInstanceUID,
                        newProjectFileName + ".zip",
                        "project",
                        zip,
                        (progress: number) => {
                            throttleUpdate(progress);
                        },
                        createDate,
                    );
                    projectUploaded = true;
                } catch (err) {
                    notifyUploadError(t("web-rg.errorWhileUploadingProject"), err as Exception);
                }

                const zipMobile = await createMobileForUpload(newProjectFileName, projectData);
                // saveAs(zipMobile, "test1.zip")
                try {
                    await api.uploadFile(
                        studyInstanceUID,
                        newProjectFileName + ".zip.mobile.json",
                        "mobile",
                        zipMobile,
                        (progress: number) => {
                            throttleUpdate(progress);
                        },
                        createDate,
                    );
                } catch (err) {
                    notifyUploadError(t("web-rg.errorWhileUploadingProject"), err as Exception);
                }

                const metaData = {
                    erp: {},
                    extra: {
                        erp: {
                            project: projectData.erpFile.project,
                        },
                        latestProjectStepShort: "New Project",
                    },
                    format: "json",
                };
                await api.uploadMetadataToProject(studyInstanceUID, newProjectFileName + ".zip", metaData);

                success(t("web-rg.projectUploadedSuccessfully", { name: newProjectFileName }));

                context.cleanup();

                //emit update patients
                emits("updatePatients", studyInstanceUID);

                safeUnlink(path);
                safeRmdir("/app");
            }
            uploadProgress.value = 100;
            uploadProgress.value = 0;
            uploadProgressVisible2.value = false;
            uploadProgressVisible.value = false;
            // dialogRef.value.close();
            return;
        } finally {
            fileUtilsWasmClass.delete();
        }
    } else {
        unFilteredFilesUpload = event.files;
        filesUpload = [];
    }

    isDeterminate.value = true;

    devLogger.debug("Time needed segment 1", performance.now() - t0);
    t0 = performance.now();

    const importFiles: DicomImportFile[] = unFilteredFilesUpload.map((file) =>
        file.data
            ? ({
                  name: file.name,
                  webkitRelativePath: file.webkitRelativePath,
                  arrayBuffer: async () => file.data.buffer,
              } as DicomImportFile)
            : file,
    );
    uploadProgressVisible.value = true;
    uploadProgressVisible1.value = true;
    const analysis = await analyzeDicomSeries({
        module,
        engine: engineInstance,
        files: importFiles,
        onProgress: (percent) => {
            processingProgress.value = Math.round(percent * 0.4);
        },
    });
    filesMap.clear();
    filesUpload = unFilteredFilesUpload;
    filesByPath = buildFilesByPathMap(filesUpload);
    previewList.value = analysis.candidates.map((candidate) => {
        if (!filesMap.has(candidate.folder)) filesMap.set(candidate.folder, new Map());
        filesMap.get(candidate.folder)!.set(candidate.id, candidate.slices);
        const middle = getDicomMiddleSlice(candidate.slices)!;
        const slice = candidate.slices.find((value) => value.fileName === middle.fileName)!;
        const metadata = slice.metadata!;
        const frame = metadata.frames[Math.max(0, middle.frameId)];
        const date = metadata.studyDate || "";
        return {
            fName: middle.fileName,
            path: candidate.folder,
            seriesId: candidate.id,
            modality: metadata.modality,
            description: metadata.description,
            position: frame.position as [number, number, number],
            thickness: metadata.thickness,
            dimensions: metadata.dimensions,
            pixelDimension: frame.pixelSpacing as [number, number],
            directionX: frame.direction.slice(0, 3) as [number, number, number],
            directionY: frame.direction.slice(3) as [number, number, number],
            instanceNumber: metadata.instanceNumber || 0,
            manufacturer: metadata.manufacturer || "",
            manufacturerModelName: metadata.manufacturerModelName || "",
            studyDate: [Number(date.slice(0, 4)), Number(date.slice(4, 6)), Number(date.slice(6, 8))] as [number, number, number],
            preview: candidate.preview || "",
            selectedFlag: ref(true),
        };
    });
    const skippedSummary = getDicomImportFailureSummary(analysis.failures);
    if (analysis.failures.length && analysis.candidates.length) {
        toast.add({ severity: "info", summary: "Some DICOM images were skipped", detail: skippedSummary, life: 8000 });
    }
    processingProgress.value = 40;
    t0 = performance.now();

    if (previewList.value.length == 0) {
        uploadProgress.value = 0;
        uploadProgressVisible2.value = false;
        uploadProgressVisible.value = false;
        filesUpload = null;
        unFilteredFilesUpload = [];
        // dialogRef.value.close();
        toast.add({ severity: "error", summary: t("web-rg.dataNotUploaded"), detail: skippedSummary || "No usable DICOM volume was found.", life: 8000 });
        return;
    }
    uploadDicomWasmClass = new engineInstance.xLoadDicom();
    previewGeneratorWasmClass = new engineInstance.xDicomPreviewGenerator();
    if (previewList.value.length < 2) {
        allProjects = 1;
        await proceedToUpload();
    } else {
        // open preview dialog
        // devLogger.debug("Previews", previewList);
        dialog.open(IPPreviewList, {
            props: {
                closable: true,
                modal: true,
                draggable: false,
                header: t("web-rg.dicomPreview"),
            },
            data: {
                previews: previewList.value,
            },
            emits: {
                onProceedToUpload: async () => {
                    await proceedToUpload();
                },
            },
        });
    }
};

let newPatientLoaded = false;
let pBDate = new Date(1970, 1, 1);
const proceedToUpload = async () => {
    try {
        let t0 = performance.now();
        allProjects = 0;

        for (let v of previewList.value) {
            if (v.selectedFlag) {
                allProjects += 1;
            }
        }
        // devLogger.debug("All Projects: ", allProjects);

        let studyInstanceUID = patientData.value.extra.study_instance_UID;

        if (!params.addNewPatientFlag && params.selectedPatient) {
            studyInstanceUID = params.selectedPatient.patientName;
        }
        let newProjectFileName = projectName.value;
        if (params.patientData?.dateOfBirth) {
            pBDate = params.patientData?.dateOfBirth;
        }

        newPatientLoaded = false;

        // prepare wasm FS folder
        ensureWasmDir("/app");
        previewList.value.forEach((project) => {
            if (project.selectedFlag) {
                const parts = project.path.split("/");
                // devLogger.debug("parts", parts);
                let currentPath = "/app";
                for (let i = 0; i < parts.length - 1; i++) {
                    currentPath += `/${parts[i]}`;
                    // devLogger.debug("Creating dir:" + currentPath);
                    ensureWasmDir(currentPath);
                }
            }
        });
        devLogger.debug("Time needed segment 5", performance.now() - t0);
        t0 = performance.now();
        // load selected files to Wasm FS
        count = 0;
        const filesByPathLocal = filesByPath ?? buildFilesByPathMap(filesUpload);
        let pendingEditPatientData: PatientListModel | null = null;
        for (let folder of filesMap) {
            for (let series of folder[1]) {
                for (let pF of previewList.value) {
                    if (pF.path == folder[0]) {
                        if (pF.seriesId == series[0]) {
                            if (pF.selectedFlag) {
                                t0 = performance.now();
                                uploadProgressVisible1.value = true;

                                count++;
                                processingProgress.value = 40 + Math.floor(((count - 1) / allProjects) * 5); // Starting project processing
                                // for series in folder --> Create and upload new project
                                uploadDicomWasmClass.PrepareForDataUpload();

                                let pathForMetadata = "";

                                // const all = series[1].length + filesUpload.length;
                                // let allCount = 0;
                                for (let slice of series[1]) {
                                    const candidates = filesByPathLocal.get(slice.fileName);
                                    if (!candidates) {
                                        continue;
                                    }
                                    for (let f of candidates) {
                                        // allCount++;
                                        // processingProgress.value = 40 + Math.floor(((allCount - 1) / all) * 10); // Starting project processing
                                        let fPathTmp = f.webkitRelativePath;
                                        let fPath = f.webkitRelativePath;
                                        if (f.webkitRelativePath.length == 0) {
                                            fPath = "/app/" + f.name;
                                            fPathTmp = f.name;
                                        } else {
                                            fPath = "/app/" + f.webkitRelativePath;
                                        }
                                        // processingProgress.value = 40 + Math.floor(((allCount - 1) / all) * 15); // Starting project processing
                                        if (fPathTmp == slice.fileName) {
                                            let buff: Int8Array;
                                            if (f.data) {
                                                buff = f.data;
                                            } else {
                                                buff = new Int8Array(await readFileAsArrayBuffer(f));
                                            }
                                            // processingProgress.value = 40 + Math.floor(((allCount - 1) / all) * 12); // Starting project processing
                                            uploadDicomWasmClass.AddToFiles(fPath, new Int8Array([1]));
                                            // uploadDicomWasmClass.AddToFiles(fPath, buff);
                                            module.FS.writeFile(fPath, buff);
                                            // processingProgress.value = 40 + Math.floor(((allCount - 1) / all) * 15); // Starting project processing
                                            if (pathForMetadata == "") {
                                                pathForMetadata = fPath;
                                            }
                                        }
                                        // processingProgress.value = 40 + Math.floor(((allCount - 1) / all) * 18); // Starting project processing
                                    }
                                }
                                processingProgress.value = 40 + Math.floor(((count - 1) / allProjects) * 10); // Starting project processing

                                // TODO GET METADATA
                                // "0010|0030": Patient Birth Date
                                const dateOfBirth = previewGeneratorWasmClass.GetDateTagFromDicomPath(
                                    pathForMetadata,
                                    dicomTags.patientBirthDate.group,
                                    dicomTags.patientBirthDate.element,
                                );
                                devLogger.debug("dateOfBirth", dateOfBirth);
                                // "0008|0030": studyDate
                                const studyDate = previewGeneratorWasmClass.GetDateTagFromDicomPath(pathForMetadata, dicomTags.studyDate.group, dicomTags.studyDate.element);
                                devLogger.debug("studyDate", studyDate);
                                // "0020|000E": seriesID
                                const seriesID = previewGeneratorWasmClass.GetTagFromDicomPath(
                                    pathForMetadata,
                                    dicomTags.seriesInstanceUID.group,
                                    dicomTags.seriesInstanceUID.element,
                                );
                                devLogger.debug("seriesID", seriesID);
                                const patientId = previewGeneratorWasmClass.GetTagFromDicomPath(pathForMetadata, dicomTags.patientId.group, dicomTags.patientId.element);
                                const studyId = previewGeneratorWasmClass.GetTagFromDicomPath(pathForMetadata, dicomTags.studyId.group, dicomTags.studyId.element);
                                const dicomStudyInstanceUID = previewGeneratorWasmClass.GetTagFromDicomPath(
                                    pathForMetadata,
                                    dicomTags.studyInstanceUID.group,
                                    dicomTags.studyInstanceUID.element,
                                );
                                // "0010|0010": patientName
                                const patientNameRaw = previewGeneratorWasmClass.GetTagFromDicomPath(pathForMetadata, dicomTags.patientName.group, dicomTags.patientName.element);
                                const dicomName = parseDicomPatientName(patientNameRaw);
                                let patientName = dicomName.fullName;
                                devLogger.debug("patientName", patientName);
                                // "0008|0060": modality
                                const modality = previewGeneratorWasmClass.GetTagFromDicomPath(pathForMetadata, dicomTags.modality.group, dicomTags.modality.element);
                                devLogger.debug("modality", modality);
                                // "0008|103E": description
                                const description = previewGeneratorWasmClass.GetTagFromDicomPath(
                                    pathForMetadata,
                                    dicomTags.seriesDescription.group,
                                    dicomTags.seriesDescription.element,
                                );
                                devLogger.debug("description", description);
                                const studyDescription = previewGeneratorWasmClass.GetTagFromDicomPath(
                                    pathForMetadata,
                                    dicomTags.studyDescription.group,
                                    dicomTags.studyDescription.element,
                                );
                                const institutionName = previewGeneratorWasmClass.GetTagFromDicomPath(
                                    pathForMetadata,
                                    dicomTags.institutionName.group,
                                    dicomTags.institutionName.element,
                                );
                                const referringPhysician = previewGeneratorWasmClass.GetTagFromDicomPath(
                                    pathForMetadata,
                                    dicomTags.referringPhysician.group,
                                    dicomTags.referringPhysician.element,
                                );
                                const kVpRaw = previewGeneratorWasmClass.GetTagFromDicomPath(pathForMetadata, dicomTags.kVp.group, dicomTags.kVp.element);
                                const kVp = Number(kVpRaw);
                                const exposureData = `${String(Number.isFinite(kVp) ? Math.round(kVp) : 0).padStart(2, " ")}kV `;
                                processingProgress.value = 40 + Math.floor(((count - 1) / allProjects) * 15); // Loading DICOM files

                                uploadDicomWasmClass.PicEditCall();

                                devLogger.debug("Time needed segment 7", performance.now() - t0);
                                t0 = performance.now();
                                const iDataView: IEngineGenerated.prototype.ImageDataJSView = uploadDicomWasmClass.GetImageDataView();

                                const image = createImageDataFromWasm(iDataView);
                                iDataView.delete();
                                // devLogger.debug("pixelData", image.getPointData().getScalars().getData());
                                devLogger.debug("dims", image.getDimensions());
                                devLogger.debug("spac", image.getSpacing());
                                devLogger.debug("org", image.getOrigin());

                                let context = new DocumentContext(assetPlugin.getAssetUrl(""));
                                context.user = user;
                                context.setResourceUrl("/resources/");
                                context.setModalsInstance(useModals());
                                context.setImageData(image);
                                context.replaceVolumeObject({ render: false });
                                context.setExternalEngineInstance(engineInstance); // needed to fully load the engine
                                processingProgress.value = 40 + Math.floor(((count - 1) / allProjects) * 20); // Processing DICOM data

                                devLogger.debug("Time needed segment 8", performance.now() - t0);
                                t0 = performance.now();
                                if (params.addNewPatientFlag && !newPatientLoaded) {
                                    newPatientLoaded = true;

                                    if (!params.patientData?.dateOfBirth) {
                                        const dicomDob = parseDicomDate(dateOfBirth);
                                        if (dicomDob) {
                                            pBDate = dicomDob;
                                        }
                                    }

                                    if (studyInstanceUID == "") {
                                        if (params.patientData && (params.patientData?.firstName.length || params.patientData?.firstName.length)) {
                                            patientName = params.patientData?.firstName + " " + params.patientData?.lastName;
                                        } else if (dicomName.fullName.length > 0) {
                                            patientName = dicomName.fullName;
                                        }
                                        studyInstanceUID = patientName.replaceAll(" ", "_") + Date.now();
                                        devLogger.debug("New Patient UID", studyInstanceUID);
                                        patientData.value.extra.study_instance_UID = studyInstanceUID;
                                        dicomPatientName = dicomName.fullName.length ? dicomName.fullName : null;
                                        dicomPatientFirstName = dicomName.firstName.length ? dicomName.firstName : null;
                                        dicomPatientLastName = dicomName.lastName.length ? dicomName.lastName : null;
                                        dicomPatientBD = parseDicomDate(dateOfBirth);
                                        // let tmpS = series[0].replaceAll(".", "").slice(0, 10);
                                        // devLogger.debug("size", pName.length);
                                        // let tmpN = pName.slice(0, Math.min(pName.length, 40)).replaceAll(" ", "_");
                                        // studyInstanceUID = (tmpN + tmpS).slice(0, 50);
                                        // devLogger.debug("Add patient", patientData.value);
                                        const created = await createPatientProject(studyInstanceUID);
                                        if (!created) return;

                                        router.push({
                                            name: "Patients",
                                            params: {
                                                id: studyInstanceUID,
                                            },
                                        });
                                        // devLogger.debug("Patient added");
                                    } else {
                                        // Data from dicom used for edit patient if created before in ImportSTL/Image/document
                                        dicomPatientName = dicomName.fullName.length ? dicomName.fullName : null;
                                        dicomPatientFirstName = dicomName.firstName.length ? dicomName.firstName : null;
                                        dicomPatientLastName = dicomName.lastName.length ? dicomName.lastName : null;
                                        dicomPatientBD = parseDicomDate(dateOfBirth);
                                    }
                                }

                                devLogger.debug("Time needed segment 9", performance.now() - t0);
                                t0 = performance.now();
                                const pMax: [number, number, number] = context.getImageData().getDimensions();

                                const today = new Date();
                                const data: ProjectGeneralData = {
                                    generalNotes: "",
                                    patientData: [(patientName || "").trim(), (patientId || "").trim(), formatDateToYYYYMMDD(parseDicomDate(dateOfBirth))].join("\n"),
                                    seriesData: [(description || "").trim(), formatDateToYYYYMMDD(parseDicomDate(studyDate)), exposureData].join("\n"),
                                    dicomData: createRGDicomData({
                                        patientName,
                                        patientId,
                                        patientStudyId: studyId || dicomStudyInstanceUID || studyInstanceUID,
                                        patientDob: formatDateToYYYYMMDD(parseDicomDate(dateOfBirth)),
                                        patientRef: referringPhysician,
                                        patientInst: institutionName,
                                        patientStudyDescr: studyDescription,
                                        patientStudyDate: convertDateToPatientStudyDateFormat(parseDicomDate(studyDate)),
                                        patientSeriesDescr: description,
                                        modality,
                                        patientStudyUid: dicomStudyInstanceUID || studyInstanceUID,
                                        isCommandLine: 0,
                                    }),
                                    modified: {
                                        year: today.getFullYear(),
                                        month: today.getMonth(),
                                        day: today.getDate(),
                                        hour: today.getHours(),
                                        minutes: today.getMinutes(),
                                        seconds: today.getSeconds(),
                                    },
                                    projectVersion: license?.versionType || 1,
                                    projectCropped: 0,
                                    croppedPointMin: [0, 0, 0],
                                    croppedPointMax: pMax,
                                    useUSToothMap: context.settings.general.useUSStyleToothChart,
                                };
                                const d2 = writeProjectObject(projectGeneralDataModel, data);
                                const originalData = {
                                    pr: d2,
                                    stlInfo: {
                                        objectType: "object",
                                        data: {},
                                    },
                                    mobile: {
                                        objectType: "object",
                                        data: {},
                                    },
                                };
                                devLogger.debug("Time needed segment 10", performance.now() - t0);
                                t0 = performance.now();

                                ////////////////////////////////////////////////////////////////////////////////////////////////////////
                                // in DicomUpload add stls from Objects3D in patientList
                                ////////////////////////////////////////////////////////////////////////////////////////////////////////
                                // TODO should be handled in IPProjectLoader if project is flagged
                                // await addObjectsFromPationt(studyInstanceUID, context);
                                ////////////////////////////////////////////////////////////////////////////////////////////////////////
                                ////////////////////////////////////////////////////////////////////////////////////////////////////////

                                devLogger.debug("Time needed segment 11", performance.now() - t0);
                                t0 = performance.now();
                                processingProgress.value = 40 + Math.floor(((count - 1) / allProjects) * 25); // Preparing project data
                                if (context.patientInfo == undefined) {
                                    context.patientInfo = {
                                        patientName: patientName,
                                        patientBirthDate: pBDate.toISOString(),
                                        patientID: studyInstanceUID,
                                    };
                                }
                                // devLogger.debug("PatientInfo", context.patientInfo);
                                const projectData = await prepareProjectData(context, originalData as Omit<ImplantPlannerProjectData, "imageData">, true);
                                processingProgress.value = 40 + Math.floor(((count - 1) / allProjects) * 30); // Project data prepared

                                devLogger.debug("Time needed segment 12", performance.now() - t0);
                                t0 = performance.now();
                                if (!projectData) {
                                    notifyUploadError(t("web-rg.errorWhileUploadingProject"), t("web-rg.projectDataNotPrepared"));
                                    continue;
                                }

                                // devLogger.debug("mobile for save:", projectData.mobile);
                                // devLogger.debug("stlinfo for save:", projectData.stlInfo);

                                const inputPatientName = `${patientData.value.firstName || ""} ${patientData.value.lastName || ""}`.trim();
                                const nameForProject = params.addNewPatientFlag && inputPatientName.length > 0 ? inputPatientName : patientName;
                                if (allProjects > 1) {
                                    // let tmpS = series[0].replaceAll(".", "").slice(0, 10);
                                    let tmpN = nameForProject.slice(0, Math.min(nameForProject.length, 40)).replaceAll(" ", "_");
                                    let tmpS = "_project" + countProj;
                                    countProj++;
                                    newProjectFileName = tmpN + tmpS;
                                    // devLogger.debug("Saving with pName: NewProjectFileName, ", newProjectFileName);
                                } else {
                                    newProjectFileName = nameForProject.replaceAll(" ", "_") + "_project";
                                }
                                newProjectFileName = await getAvailableProjectName(newProjectFileName);
                                if (!params.addNewPatientFlag) {
                                    projectName.value = newProjectFileName;
                                }

                                projectNameProgressBar.value = newProjectFileName;

                                const throttleUpdate = useThrottleFn((progress: number) => {
                                    uploadProgress.value = Math.ceil(((count - 1) / allProjects) * 100 + (progress * 100) / allProjects) - 1;
                                }, 250);

                                devLogger.debug("Time needed segment 13", performance.now() - t0);
                                t0 = performance.now();

                                processingProgress.value = 40 + Math.floor(((count - 1) / allProjects) * 35); // Project data prepared

                                const throttleUpdate2 = useThrottleFn((metadata: { percent: number; currentFile: string | null }) => {
                                    // devLogger.debug("ZIP UPDATE PROGRESS", metadata);
                                    processingProgress.value = 40 + 35 + Math.floor(metadata.percent * 20) - 1;
                                }, 250);
                                const zip = await createProjectZip(newProjectFileName, projectData, throttleUpdate2);
                                // saveAs(zip, "test0.zip")
                                processingProgress.value = 40 + Math.floor(((count - 1) / allProjects) * 55); // Project data prepared
                                const createDate = new Date().toISOString();

                                processingProgress.value = 40 + Math.floor(((count - 1) / allProjects) * 60); // Project data prepared

                                uploadProgressVisible1.value = false;
                                uploadProgressVisible2.value = true;

                                devLogger.debug("Time needed segment 14 createZip 1", performance.now() - t0);
                                t0 = performance.now();
                                // devLogger.debug("Upload Project files sInstanceUID", studyInstanceUID, " newProjectFileName", newProjectFileName);
                                let projectUploaded = false;
                                try {
                                    await api.uploadFile(
                                        studyInstanceUID,
                                        newProjectFileName + ".zip",
                                        "project",
                                        zip,
                                        (progress: number) => {
                                            throttleUpdate(progress);
                                        },
                                        createDate,
                                    );
                                    projectUploaded = true;
                                } catch (err) {
                                    notifyUploadError(t("web-rg.errorWhileUploadingProject"), err as Exception);
                                }

                                devLogger.debug("Time needed segment 15 upload zip", performance.now() - t0);
                                t0 = performance.now();

                                const zipMobile = await createMobileForUpload(newProjectFileName, projectData);
                                // saveAs(zipMobile, "test1.zip")
                                devLogger.debug("Time needed segment 16 create mobile", performance.now() - t0);
                                t0 = performance.now();
                                try {
                                    await api.uploadFile(
                                        studyInstanceUID,
                                        newProjectFileName + ".zip.mobile.json",
                                        "mobile",
                                        zipMobile,
                                        (progress: number) => {
                                            throttleUpdate(progress);
                                        },
                                        createDate,
                                    );
                                    context.cleanup();
                                } catch (err) {
                                    notifyUploadError(t("web-rg.errorWhileUploadingProject"), err as Exception);
                                }
                                const metaData = {
                                    erp: {},
                                    extra: {
                                        erp: {
                                            project: projectData.erpFile.project,
                                        },
                                        latestProjectStepShort: "New Project",
                                    },
                                    format: "json",
                                };
                                await api.uploadMetadataToProject(studyInstanceUID, newProjectFileName + ".zip", metaData);

                                devLogger.debug("Time needed segment 17 upload mobile", performance.now() - t0);
                                t0 = performance.now();

                                success(t("web-rg.projectUploadedSuccessfully", { name: newProjectFileName }));

                                //emit update patients
                                emits("updatePatients", studyInstanceUID);

                                // Data from dicom used for edit patient if created before
                                if ((dicomPatientName || dicomPatientBD || dicomPatientFirstName || dicomPatientLastName) && !pendingEditPatientData) {
                                    const newPatientData = JSON.parse(JSON.stringify(patientData.value));
                                    if (typeof newPatientData.dateOfBirth === "string") {
                                        newPatientData.dateOfBirth = new Date(newPatientData.dateOfBirth);
                                    }
                                    if (dicomPatientFirstName || dicomPatientLastName) {
                                        newPatientData.firstName = dicomPatientFirstName || "";
                                        newPatientData.lastName = dicomPatientLastName || "";
                                        newPatientData.extra.patientName = `${newPatientData.firstName} ${newPatientData.lastName}`.trim();
                                    } else if (dicomPatientName) {
                                        newPatientData.firstName = dicomPatientName;
                                        newPatientData.lastName = "";
                                        newPatientData.extra.patientName = dicomPatientName;
                                    }
                                    if (dicomPatientBD) {
                                        newPatientData.dateOfBirth = dicomPatientBD;
                                    }
                                    newPatientData.extra.modality = modality;
                                    pendingEditPatientData = newPatientData;
                                }
                            } else {
                                devLogger.debug("Skipping unselected Series");
                            }
                        }
                    }
                }
            }
        }
        uploadProgress.value = 100;
        uploadProgress.value = 0;
        uploadProgressVisible2.value = false;
        uploadProgressVisible.value = false;

        // cleaning Wasm FS
        for (let f of filesUpload) {
            let fPath = f.webkitRelativePath;
            if (f.webkitRelativePath.length == 0) {
                fPath = "/app/" + f.name;
            } else {
                fPath = "/app/" + f.webkitRelativePath;
            }
            // devLogger.debug("Unlinking - ", fPath);
            safeUnlink(fPath);
        }
        previewList.value.forEach((project) => {
            if (project.selectedFlag) {
                const orgPath = "/app/" + project.path;
                // devLogger.debug("orgPath", orgPath);
                // devLogger.debug("removing: orgPath", orgPath);
                safeRmdir(orgPath);
                const parts = project.path.split("/");
                // devLogger.debug("parts", parts);
                if (parts.length > 1) {
                    let currentPath = orgPath;
                    for (let i = parts.length - 2; i >= 0; i--) {
                        let l = parts[i].length;
                        if (l > 0) {
                            l += 1;
                        }
                        currentPath = currentPath.slice(0, currentPath.length - l);
                        // devLogger.debug("removing dir:" + currentPath);
                        safeRmdir(currentPath);
                    }
                }
            }
        });
        filesUpload = null;
        unFilteredFilesUpload = [];

        // dialogRef.value.close();
        //emits("done");
        devLogger.debug("DONE");

        if (pendingEditPatientData) {
            dialog.open(EditPatientDialog, {
                props: {
                    closable: true,
                    modal: true,
                    draggable: false,
                    header: t("web-rg.editPatient"),
                    style: { width: "50vw" },
                    breakpoints: { "640px": "95vw" },
                },
                data: {
                    patientData: pendingEditPatientData,
                    doctors: [],
                },
                emits: {
                    onPatientUpdated: async () => {
                        //emit update patients
                        emits("updatePatients", studyInstanceUID);
                    },
                },
            });
        }
    } finally {
        if (uploadDicomWasmClass) {
            uploadDicomWasmClass.delete();
        }
        if (previewGeneratorWasmClass) {
            previewGeneratorWasmClass.delete();
        }
    }
};
const showForm = computed(() => {
    return state.value !== UIState.LOADING;
});

const closeDialog = (study_instance_UID: string) => {
    emits("updatePatients", study_instance_UID);
    // dialogRef.value.close();
};

const createPatient = async () => {
    const studyInstanceUID = (patientData.value.firstName + " " + patientData.value.lastName).replaceAll(" ", "_") + Date.now();
    // devLogger.debug("Add patient createPatient", studyInstanceUID);
    patientData.value.extra.study_instance_UID = studyInstanceUID;
    const created = await createPatientProject(studyInstanceUID);
    if (!created) return;
    router.push({
        name: "Patients",
        params: {
            id: studyInstanceUID,
        },
    });
};

const onSelectedFiles = async (selection: File[]) => {
    if (!selection?.length || !isFolderSelection(selection)) {
        return;
    }
    const uploadEvent = { files: selection } as FileUploadUploaderEvent;
    try {
        const didUpload = await confirmation(uploadEvent);
        if (didUpload) {
            lastAutoUploadedKey.value = getSelectionKey(selection);
        }
    } catch (err) {
        notifyUploadError(t("web-rg.errorWhileUploadingProject"), err as Exception);
    }
};
</script>

<style scoped>
:deep(.p-inputtext) {
    border: 2px #868c9b solid !important;
}

:deep(.p-divider-vertical:before) {
    border-left: 1px solid rgba(255, 255, 255, 0.05) !important;
}

:deep(.p-fileupload-header) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    margin-bottom: 1.25rem;
}

:deep(.p-badge.p-component.p-badge-warn.p-fileupload-file-badge) {
    display: none !important;
}

:deep(.p-fileupload-file-thumbnail) {
    flex-grow: 1;
    display: none;
}

:deep(.web-rg-wiz-btn .p-button) {
    border-radius: 16px !important;
}
</style>

<template>
    <div class="w-full">
        <Divider class="mt-1" />
        <MContainer v-show="state !== UIState.LOADING">
            <RgImportMenu @setActive="state = $event" />
        </MContainer>
        <Divider />
        <MContainerRow v-if="state === UIState.UPLOADING_DICOM" v-show="showForm">
            <UniversalFilePicker
                class="grow"
                :dropArea="true"
                :selectFilesBtn="true"
                :selectFolderBtn="true"
                :accept="dicomFormats"
                :writeToWasmFS="false"
                :context="processingContext ? (processingContext as DocumentContext) : undefined"
                objectName="test2"
                :singleSelection="true"
                :importTmpStls="true"
                :embedded="true"
                :showAccept="true"
                :showTypeColumn="false"
                :hideTreeWhenNoFolders="true"
                :disabled="!isEngineReady"
                @uploadFiles="onUploadFiles"
                @selectedFiles="onSelectedFiles" />
        </MContainerRow>
        <StlObject
            v-if="state === UIState.UPLOADING_OBJECT"
            :patientData="params.addNewPatientFlag ? patientData : undefined"
            :patientName="params.selectedPatient ? params.selectedPatient.patientName : ''"
            @done="closeDialog"
            :createPatient="createPatient" />
        <ImportImage
            v-if="state === UIState.UPLOADING_IMAGE"
            :patientData="params.addNewPatientFlag ? patientData : undefined"
            :patientName="params.selectedPatient ? params.selectedPatient.patientName : ''"
            @done="closeDialog"
            :createPatient="createPatient" />
        <ImportDocument
            v-if="state === UIState.UPLOADING_DOCUMENT"
            :patientData="params.addNewPatientFlag ? patientData : undefined"
            :patientName="params.selectedPatient ? params.selectedPatient.patientName : ''"
            :createPatient="createPatient"
            @done="closeDialog" />
        <Dialog
            v-model:visible="uploadProgressVisible"
            :modal="true"
            :closable="false"
            :draggable="false"
            :closeOnEscape="false"
            :style="{ width: '50vw' }"
            :header="$t('shared.uploadingFiles')">
            <template v-if="uploadProgressVisible1">
                <div>
                    <p>{{ $t("web-rg.processingFiles") }}</p>
                    <ProgressBar
                        :mode="isDeterminate ? 'determinate' : 'indeterminate'"
                        :value="processingProgress"
                        :showValue="false"
                        class="mt-4"
                        style="height: 2rem"
                        :pt="{ value: { style: { transition: 'none' } } }">
                        {{ processingProgress.toFixed(0) }} %
                    </ProgressBar>
                </div>
            </template>
            <template v-if="uploadProgressVisible2">
                <div>
                    <p>{{ $t("shared.uploadingProject") }} {{ projectNameProgressBar }} [{{ count }}/{{ allProjects }}]</p>
                    <ProgressBar :value="uploadProgress" :showValue="false" class="mt-4" style="height: 2rem" :pt="{ value: { style: { transition: 'none' } } }">
                        {{ uploadProgress.toFixed(0) }} %
                    </ProgressBar>
                </div>
            </template>
        </Dialog>
    </div>
</template>
